import { isValid, parse } from 'date-fns';
import type { DateStringValue } from '@mantine/dates';

/**
 * 行照／駕照上印的是民國年，審核人員以民國年檢視，送給後端的一律是西元 YYYY-MM-DD。
 * 對外一律吃 Mantine 的 DateStringValue（YYYY-MM-DD），Date 只在這裡內部使用。
 * 民國元年以前的日期由 RocDateInput 的 minDate 擋掉，這裡不另外處理。
 */

const rocFormatter = new Intl.DateTimeFormat('zh-TW-u-ca-roc', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

const parseDateString = (value: DateStringValue | null | undefined) => {
  if (!value) return null;
  const date = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(date) ? date : null;
};

/** 拆成 { era: '民國', year: '90', month: '1', day: '1' } */
const toRocParts = (value: DateStringValue | null | undefined) => {
  const date = parseDateString(value);
  if (!date) return null;

  const parts = rocFormatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return {
    era: get('era'),
    year: get('year'),
    month: get('month'),
    day: get('day'),
  };
};

const pad2 = (value: string) => value.padStart(2, '0');

/** 民國 90 年 01 月 01 日 */
const toRocDateLabel = (value: DateStringValue | null | undefined) => {
  const roc = toRocParts(value);
  if (!roc) return '';

  return `${roc.era} ${roc.year} 年 ${pad2(roc.month)} 月 ${pad2(roc.day)} 日`;
};

/** 日曆標頭用：民國 90 年 1 月 */
const toRocMonthLabel = (value: DateStringValue) => {
  const roc = toRocParts(value);
  return roc ? `${roc.era} ${roc.year} 年 ${roc.month} 月` : value;
};

/** 日曆標頭用：民國 90 年 */
const toRocYearLabel = (value: DateStringValue) => {
  const roc = toRocParts(value);
  return roc ? `${roc.era} ${roc.year} 年` : value;
};

/** 日曆標頭用：民國 109 - 118 年 */
const toRocDecadeLabel = (start: DateStringValue, end: DateStringValue) => {
  const startRoc = toRocParts(start);
  const endRoc = toRocParts(end);
  if (!startRoc || !endRoc) return `${start} - ${end}`;

  return `${startRoc.era} ${startRoc.year} - ${endRoc.year} 年`;
};

/**
 * 十年檢視的年份格子用：109。
 * 格子的預設文字只吃 dayjs 格式字串（做不出民國年），要靠 getYearControlProps 的 children 覆蓋。
 */
const toRocYearCellLabel = (value: DateStringValue) =>
  toRocParts(value)?.year ?? value;

export {
  toRocDateLabel,
  toRocDecadeLabel,
  toRocMonthLabel,
  toRocYearCellLabel,
  toRocYearLabel,
};
